import ajax from '@/lib/ajax';
import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import CompanyDto from '@/models/Companies/CompanyDto';
import store from '@/store';
import { AppModule } from '@/store/modules/app';
import UpdateCompanyGeneralDetailsRequest from '@/models/Companies/UpdateCompanyGeneralDetailsRequest';
import UpdateCompanyProfileDetailsRequest from '@/models/Companies/UpdateCompanyProfileDetailsRequest';
import UpdateLocationGeneralDetailsRequest from '@/models/Locations/UpdateLocationGeneralDetailsRequest';
import UpdateLocationProfileDetailsRequest from '@/models/Locations/UpdateLocationProfileDetailsRequest';
import UpdateCompanyAddressDetailsRequest from '@/models/CompanyAddresses/UpdateCompanyAddressDetailsRequest';
import UpdateContactRequest from '@/models/Contacts/UpdateContactRequest';
import CreateContactRequest from '@/models/Contacts/CreateContactRequest';
import CreateCompanyAddressRequest from '@/models/CompanyAddresses/CreateCompanyAddressRequest';
import ContactDto from '@/models/Contacts/ContactDto';
import BookingDto from '@/models/Bookings/BookingDto';
import NoteDto from '@/models/Notes/NoteDto';
import UpdateNoteRequest from '@/models/Notes/UpdateNoteRequest';
import CreateNoteRequest from '@/models/Notes/CreateNoteRequest';
import LocationDto from '@/models/Locations/LocationDto';
import CreateLocationRequest from '@/models/Locations/CreateLocationRequest';
import CompanyAddressDto from '@/models/CompanyAddresses/CompanyAddressDto';
import RateCardDto from '@/models/RateCards/RateCardDto';
import CreateRateCardRequest from '@/models/RateCards/CreateRateCardRequest';
import UpdateRateCardRequest from '@/models/RateCards/UpdateRateCardRequest';
import RateCardPositionDto from '@/models/RateCardPositions/RateCardPositionDto';
import UpdateRateCardPositionRequest from '@/models/RateCardPositions/UpdateRateCardPositionRequest';
import CreateRateCardPositionRequest from '@/models/RateCardPositions/CreateRateCardPositionRequest';
import StorageUploadResponse from '@/models/Storage/StorageUploadResponse';
import DocumentDto from '@/models/Documents/DocumentDto';
import UploadDocumentRequest from '@/models/Documents/UploadDocumentRequest';
import CreateDocumentRequest from '@/models/Documents/CreateDocumentRequest';
import UpdateDocumentRequest from '@/models/Documents/UpdateDocumentRequest';
import FileUtils from '@/lib/fileUtils';
import FileWithExpiry from '@/models/Documents/FileWithExpiry';
import NoteCategoryDto from '@/models/NoteCategories/NoteCategoryDto';
import { XeroContactSearchRequest, XeroContactSearchResponse } from '@/models/Invoicing/XeroContactSearchResult';
import UpdateCompanyAccountingDetailsRequest from '@/models/Companies/UpdateCompanyAccountingDetailsRequest';

export interface ICompanyDetailsState {
  loading: boolean;
  currentCompany: CompanyDto | null;
  bookings: BookingDto[];
  notes: NoteDto[];
  noteCategories: NoteCategoryDto[];
  loadingNotes: boolean;
  currentContact: ContactDto;
  contacts: ContactDto[];
  loadingContacts: boolean;
  locations: LocationDto[];
  currentLocation: LocationDto;
  loadingLocations: boolean;
  companyAddresses: CompanyAddressDto[];
  currentCompanyAddress: CompanyAddressDto;
  loadingCompanyAddresses: boolean;
  loadingBookings: boolean;
  rateCards: RateCardDto[];
  locationRateCards: RateCardDto[];
  loadingRateCards: boolean;
  currentRateCard: RateCardDto;
  loadingNewBookingWizardPage: boolean;
  documents: DocumentDto[];
  storageUploadResponse: StorageUploadResponse;
  loadingDocuments: boolean;
  loadingDialog: boolean;
  
}

@Module({ dynamic: true, store, name: 'CompanyDetails', namespaced: true})
class CompanyDetails extends VuexModule implements ICompanyDetailsState {
  
  public currentCompany: CompanyDto = new CompanyDto();
  public bookings: BookingDto[] = [];
  public notes: NoteDto[] = [];
  public noteCategories: NoteCategoryDto[] = [];
  public currentContact: ContactDto = new ContactDto();
  public contacts: ContactDto[] = [];  
  public loadingContacts = true;
  public locations: LocationDto[] = [];
  public currentLocation: LocationDto = new LocationDto();
  public loadingLocations = true;
  public companyAddresses: CompanyAddressDto[] = [];
  public currentCompanyAddress: CompanyAddressDto = new CompanyAddressDto();
  public loadingCompanyAddresses = true;
  public loadingBookings = true;
  public loadingNewBookingWizardPage = false;
  public rateCards: RateCardDto[] =[];
  public locationRateCards: RateCardDto[] =[];
  public loadingRateCards = true;
  public currentRateCard: RateCardDto = new RateCardDto();
  public loadingDialog = false;
  public loading = true;
  public documents: DocumentDto[] = [];
  public storageUploadResponse: StorageUploadResponse = null;
  public loadingDocuments = true;
  public loadingNotes = true;
  public loadingXeroContacts = false;
  public xeroContactSearchResults: XeroContactSearchResponse = new XeroContactSearchResponse();

  @Action
  public async getCompany(company: CompanyDto) {
    this.setLoading(true);
    await ajax.get(`/api/v1/agency/companies/${company.id}`)
    .then(response => {
      this.context.commit("setCurrentCompany", response.data);
      this.setLoading(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoading(false);
    });
  }

  @Action
  public async editCompanyGeneralDetails(request: UpdateCompanyGeneralDetailsRequest) {
    this.setLoadingDialog(true); 
    await ajax.put(`/api/v1/agency/companies/${this.currentCompany.id}/general-details`, request)
    .then(response => {
      this.context.commit("setCurrentCompany", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    })
  }

  @Action
  public async editCompanyProfileDetails(request: UpdateCompanyProfileDetailsRequest) {
    this.setLoadingDialog(true); 
    await ajax.put(`/api/v1/agency/companies/${this.currentCompany.id}/profile-details`, request)
    .then(response => {
      this.context.commit("setCurrentCompany", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    })
  }

    @Action
  async editCompanyAccountingDetails(request: UpdateCompanyAccountingDetailsRequest) {
    this.setLoadingDialog(true);
     await ajax.put(`/api/v1/agency/companies/${this.currentCompany.id}/accounting-details`, request)
    .then(response => {
      this.context.commit("setCurrentCompany", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    })
  }

  @Action
  async getBookings() {
    this.setLoadingBookings(true);
    //Can't use currentCompany.id here yet because the request might have come from the Agency Booking List
    await ajax.get(`/api/v1/agency/companies/${this.currentCompany.id}/bookings`)
    .then(response => {
      this.context.commit("setBookings", response.data);
      this.setLoadingBookings(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingBookings(false);
    });
  }

  @Action
  async getNoteCategories() {
    //this.setLoadingNotes(true);
    await ajax.get(`/api/v1/agency/note-categories?entityType=4`)
    .then(response => {
      this.context.commit("setNoteCategories", response.data);
      //this.setLoadingNotes(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      //this.setLoadingNotes(false);
    });
  }

  @Action
  async getNotes() {
    this.setLoadingNotes(true);
    await ajax.get(`/api/v1/agency/companies/${this.currentCompany.id}/notes`)
    .then(response => {
      this.context.commit("setNotes", response.data);
      this.setLoadingNotes(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingNotes(false);
    });
  }

  @Action
  async createNote(request: CreateNoteRequest) {
    this.setLoadingDialog(true);
    await ajax.post(`/api/v1/agency/companies/${this.currentCompany.id}/notes`, request)
    .then(response => {
      this.context.commit("setNoteFromCreate", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  async editNote(note: NoteDto) {
    const request = new UpdateNoteRequest();
    request.updatedAt = note.updatedAt
    request.description = note.description;
    this.setLoadingDialog(true);
    await ajax.put(`/api/v1/agency/companies/${this.currentCompany.id}/notes/${note.id}`, request)
    .then(response => {
      this.context.commit("setNoteFromEdit", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }


  @Action
  async getDocuments() {
    this.setLoadingDocuments(true);
    await ajax.get(`/api/v1/agency/companies/${this.currentCompany.id}/documents`)
    .then(response => {
      this.context.commit("setDocuments", response.data);
      this.setLoadingDocuments(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDocuments(false);
    });
  }

  @Action
  async uploadDocument(fileWithExpiry: FileWithExpiry) {

    const fileExtension = "." + fileWithExpiry.file.name.split('.').pop();
    const contentType = FileUtils.cleanContentType(fileExtension, fileWithExpiry.file.type);
    //First get the signed URL
    const uploadRequest = new UploadDocumentRequest();
    uploadRequest.fileName = fileWithExpiry.file.name;
    uploadRequest.contentType = contentType;
    let destinationFileName = '';

    this.setLoadingDialog(true);
    //this.context.commit("setDocumentUploadUrl", null);
    await ajax.post(`/api/v1/agency/companies/${this.currentCompany.id}/documents/upload`, uploadRequest)
    .then(uploadResponse => {
      const config = {
        headers: {
          'content-type': contentType
        }
      };
      destinationFileName = uploadResponse.data.destinationFileName;
      return ajax.put(uploadResponse.data.signedUrl, fileWithExpiry.file, config)})
      .then(googleResponse => {
        const createRequest = new CreateDocumentRequest();
        createRequest.originFileName = fileWithExpiry.file.name;
        createRequest.contentType = contentType;
        createRequest.destinationFileName = destinationFileName;
        createRequest.isExpiryTracked = fileWithExpiry.isExpiryTracked;
        createRequest.expiresAt = fileWithExpiry.expiresAt;
        ajax.post(`/api/v1/agency/companies/${this.currentCompany.id}/documents`, createRequest)
        .then(response => {
          this.context.commit("setDocumentFromUpload", response.data);
          this.setLoadingDialog(false);
        })
      })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  async downloadDocument(id: string) {
    this.setLoadingDialog(true);
    await ajax.get(`/api/v1/agency/companies/${this.currentCompany.id}/documents/${id}`)
    .then(response => {
      //download the file
      const element = document.createElement('a');
      element.setAttribute('href', response.data.signedUrl);

      element.style.display = 'none';
      document.body.appendChild(element);

      element.click();

      document.body.removeChild(element);
          
    })
    .catch(error => {
      AppModule.handleApiError(error);
    });
  }

  @Action
  async editDocument(document: DocumentDto) {
    const request = new UpdateDocumentRequest();
    request.updatedAt = document.updatedAt;
    request.name = document.name;
    request.isExpiryTracked = document.isExpiryTracked;
    if (document.expiresAt !== null) {
      request.expiresAt = new Date(document.expiresAt).toISOString().substr(0, 10)
    } 
    this.setLoadingDialog(true);
    await ajax.put(`/api/v1/agency/companies/${this.currentCompany.id}/documents/${document.id}`, request)
    .then(response => {
      this.context.commit("setDocumentFromEdit", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }
  
  @Action
  async searchXeroContacts(request: XeroContactSearchRequest) {
    this.setLoadingXeroContacts(true);
    // Use the correct parameter name that matches what the backend expects
    return ajax.get(`/api/v1/agency/xero/contacts/search?search=${request.searchTerm}`)
      .then(response => {
        console.log('Raw response data:', response.data); // Add debug log
        this.context.commit("setXeroContactSearchResults", response.data);
        this.setLoadingXeroContacts(false);
        return response.data;
      })
      .catch(error => {
        console.error('API error:', error); // Add debug log
        AppModule.handleApiError(error);
        this.setLoadingXeroContacts(false);
        throw error;
      });
  }

  @Action
  async getContacts() {
    this.setLoadingContacts(true);
    await ajax.get(`/api/v1/agency/companies/${this.currentCompany.id}/contacts`)
    .then(response => {
      this.context.commit("setContacts", response.data);
      this.setLoadingContacts(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingContacts(false);
    });
  }

  @Action
  public async updateCurrentContact(contact: ContactDto){
    this.context.commit("setCurrentContact", contact);
  }

  @Action
  public async editContact(contact: UpdateContactRequest) {
    this.setLoadingDialog(true); 
    await ajax.put(`/api/v1/agency/companies/${this.currentCompany.id}/contacts/${this.currentContact.id}`, contact)
    .then(response => {
      this.context.commit("setContactFromEdit", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    })
  }

  @Action
  public async createContact(request: CreateContactRequest) {
    this.setLoadingDialog(true); 
    await ajax.post(`/api/v1/agency/companies/${this.currentCompany.id}/contacts`, request)
    .then(response => {
      this.context.commit("setContactFromCreate", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    })
  }

  @Action
  public async getLocations() {
    this.setLoadingLocations(true);
    await ajax.get(`/api/v1/agency/companies/${this.currentCompany.id}/locations`)
    .then(response => {
      this.context.commit("setLocations", response.data);
      this.setLoadingLocations(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingLocations(false);
    });
  }

  @Action
  public async editLocationGeneralDetails(request: UpdateLocationGeneralDetailsRequest) {
    this.setLoadingDialog(true); 
    await ajax.put(`/api/v1/agency/companies/${this.currentCompany.id}/locations/${this.currentLocation.id}/general-details`, request)
    .then(response => {
      this.context.commit("setLocationFromEdit", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    })
  }

  @Action
  public async editLocationProfileDetails(request: UpdateLocationProfileDetailsRequest) {
    this.setLoadingDialog(true); 
    await ajax.put(`/api/v1/agency/companies/${this.currentCompany.id}/locations/${this.currentLocation.id}/profile-details`, request)
    .then(response => {
      this.context.commit("setLocationFromEdit", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    })
  }

  @Action
  public async updateCurrentLocation(location: LocationDto){
    this.context.commit("setCurrentLocation", location);
  }

  @Action
  public async createLocation(request: CreateLocationRequest) {
    this.setLoadingDialog(true); 
    await ajax.post(`/api/v1/agency/companies/${this.currentCompany.id}/locations`, request)
    .then(response => {
      this.context.commit("setLocationFromCreate", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    })
  }

  @Action
  public async getCompanyAddresses() {
    this.setLoadingCompanyAddresses(true);
    await ajax.get(`/api/v1/agency/companies/${this.currentCompany.id}/addresses`)
    .then(response => {
      this.context.commit("setCompanyAddresses", response.data);
      this.setLoadingCompanyAddresses(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingCompanyAddresses(false);
    });
  }

  @Action
  public async editCompanyAddressDetails(request: UpdateCompanyAddressDetailsRequest) {
    this.setLoadingDialog(true); 
    await ajax.put(`/api/v1/agency/companies/${this.currentCompany.id}/addresses/${this.currentCompanyAddress.id}`, request)
    .then(response => {
      this.context.commit("setCompanyAddressFromEdit", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    })
  }

  @Action
  public async updateCurrentCompanyAddress(companyAddress: CompanyAddressDto){
    this.context.commit("setCurrentCompanyAddress", companyAddress);
  }

  @Action
  public async createCompanyAddress(request: CreateCompanyAddressRequest) {
    this.setLoadingDialog(true); 
    await ajax.post(`/api/v1/agency/companies/${this.currentCompany.id}/addresses`, request)
    .then(response => {
      this.context.commit("setCompanyAddressFromCreate", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    })
  }

  @Action
  public async createRateCard(request: CreateRateCardRequest) {
    this.setLoadingDialog(true); 
    await ajax.post(`/api/v1/agency/companies/${this.currentCompany.id}/rate-cards`, request)
    .then(response => {
      this.context.commit("setRateCardFromCreate", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    })
  }

  @Action
  public async editRateCard(request: UpdateRateCardRequest) {
    this.setLoadingDialog(true); 
    await ajax.put(`/api/v1/agency/companies/${this.currentCompany.id}/rate-cards/${this.currentRateCard.id}`, request)
    .then(response => {
      this.context.commit("setRateCardFromEdit", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    })
  }

  @Action
  async getRateCards() {
    this.setLoadingRateCards(true);
    await ajax.get(`/api/v1/agency/companies/${this.currentCompany.id}/rate-cards`)
    .then(response => {
      this.context.commit("setRateCards", response.data);
      this.setLoadingRateCards(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingRateCards(false);
    });
  }

  @Action
  async getLocationRateCards() {
    this.setLoading(true);
    await ajax.get(`/api/v1/agency/companies/${this.currentCompany.id}/location-rate-cards`)
    .then(response => {
      this.context.commit("setLocationRateCards", response.data );
      this.setLoading(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoading(false);
    });
  }

  @Action
  public async updateCurrentRateCard(rateCard: RateCardDto){
    this.context.commit("setCurrentRateCard", rateCard);
  }

  @Action
  public async createRateCardPosition(request: CreateRateCardPositionRequest) {
    this.setLoadingDialog(true); 
    await ajax.post(`/api/v1/agency/companies/${this.currentCompany.id}/rate-cards/${this.currentRateCard.id}/positions`, request)
    .then(response => {
      this.context.commit("setRateCardPositionFromCreate", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    })
  }

  @Action
  public async editRateCardPosition(rateCardPosition: RateCardPositionDto) {
    this.setLoadingDialog(true); 
    const request = new UpdateRateCardPositionRequest();
    request.updatedAt = rateCardPosition.updatedAt;
    request.chargeRate = rateCardPosition.chargeRate;
    await ajax.put(`/api/v1/agency/companies/${this.currentCompany.id}/rate-cards/${this.currentRateCard.id}/positions/${rateCardPosition.id}`, request)
    .then(response => {
      this.context.commit("setRateCardPositionFromEdit", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    })
  }

  @Action 
  async deleteRateCardPosition(rateCardPosition: RateCardPositionDto) {
    //this.setLoading(true);
    await ajax.delete(`/api/v1/agency/companies/${this.currentCompany.id}/rate-cards/${this.currentRateCard.id}/positions/${rateCardPosition.id}`)
    .then(response => {
      this.context.commit("removeRateCardPositionFromDelete", rateCardPosition);
      //this.setLoading(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      //this.setLoading(false);
    });
  }

  @Action
  cleanState() {
    this.context.commit("resetState");
  }

  @Mutation
  resetState() {
    this.currentCompany = new CompanyDto();
    this.bookings = [];
    this.currentContact = new ContactDto();
    this.contacts = [];
    this.currentLocation = new LocationDto();
    this.locations = [];
    this.currentCompanyAddress = new CompanyAddressDto();
    this.companyAddresses = [];
    this.notes = [];
    this.noteCategories = [];
    this.documents = [];
    this.bookings = [];
    this.rateCards = [];
    this.currentRateCard = new RateCardDto();
    this.loading = true;
    this.loadingContacts = true;
    this.loadingLocations = true;
    this.loadingCompanyAddresses = true;
    this.loadingNotes = true;
    this.loadingDocuments = true;
    this.loadingBookings = true;
    this.loadingRateCards = true;
    this.loadingDialog = false;
    this.loadingNewBookingWizardPage = false;
  }

  @Mutation
  private async setLoading(isLoading: boolean) {
      this.loading = isLoading;
  }
  
  @Mutation
  private async setCurrentCompany(company: CompanyDto) {
      this.currentCompany = company;
  }

  @Mutation
  private async setLoadingContacts(isLoading: boolean) {
      this.loadingContacts = isLoading;
  }

  @Mutation
  private async setLoadingLocations(isLoading: boolean) {
      this.loadingLocations = isLoading;
  }

  @Mutation
  private async setLoadingCompanyAddresses(isLoading: boolean) {
      this.loadingCompanyAddresses = isLoading;
  }

  @Mutation
  private async setLoadingDialog(isLoading: boolean) {
      this.loadingDialog = isLoading;
  }
  
  @Mutation
  private async setContacts(contacts: ContactDto[]) {
      this.contacts = contacts;
      if (contacts && contacts.length > 0) {
        this.currentContact = this.contacts[0];
      }
  }
  
  @Mutation
  private async setCurrentContact(contact: ContactDto) {
      this.currentContact = contact;
  }

  @Mutation
  private async setContactFromEdit(contact: ContactDto) {
    this.currentContact = contact;
    const index = this.contacts.findIndex((findContact: ContactDto) => contact.id === findContact.id);
    this.contacts[index] = contact;
  }

  @Mutation
  private async setContactFromCreate(contact: ContactDto) {
    this.contacts.push(contact);
    this.currentContact = contact;
  }

  @Mutation
  private async setLocations(locations: LocationDto[]) {
      this.locations = locations;
      if (locations && locations.length > 0) {
        this.currentLocation = this.locations[0];
      }
  }

  @Mutation
  private async setLocationFromEdit(location: LocationDto) {
    this.currentLocation = location;
    const index = this.locations.findIndex((findLocation: LocationDto) => location.id === findLocation.id);
    this.locations[index] = location;
  }

  @Mutation
  private async setLocationFromCreate(location: LocationDto) {
    this.currentLocation = location;
    this.locations.push(location);
  }
    
  @Mutation
  private async setCurrentLocation(location: LocationDto) {
      this.currentLocation = location;
  }

  @Mutation
  private async setCompanyAddresses(companyAddresses: CompanyAddressDto[]) {
      this.companyAddresses = companyAddresses;
      if (companyAddresses && companyAddresses.length > 0) {
        this.currentCompanyAddress = this.companyAddresses[0];
      }
  }

  @Mutation
  private async setCompanyAddressFromEdit(companyAddress: CompanyAddressDto) {
    this.currentCompanyAddress = companyAddress;
    const index = this.companyAddresses.findIndex((findAddress: CompanyAddressDto) => companyAddress.id === findAddress.id);
    this.companyAddresses[index] = companyAddress;
  }

  @Mutation
  private async setCompanyAddressFromCreate(companyAddress: CompanyAddressDto) {
    this.currentCompanyAddress = companyAddress;
    this.companyAddresses.push(companyAddress);
  }
    
  @Mutation
  private async setCurrentCompanyAddress(companyAddress: CompanyAddressDto) {
      this.currentCompanyAddress = companyAddress;
  }


  @Mutation
  private async setBookings(bookings: BookingDto[]) {
      this.bookings = bookings;
  }

  @Mutation
  private async setCurrentRateCard(rateCard: RateCardDto) {
      this.currentRateCard = rateCard;
  }

  @Mutation
  private async setLoadingRateCards(isLoading: boolean) {
      this.loadingRateCards = isLoading;
  }

  @Mutation
  private async setRateCards(rateCards: RateCardDto[]) {
      this.rateCards = rateCards;
      if (rateCards && rateCards.length > 0) {
        this.currentRateCard = this.rateCards[0];
      } else {
        this.currentRateCard = new RateCardDto();
      }
  }

  @Mutation
  private async setLocationRateCards(rateCards: RateCardDto[]) {
      this.locationRateCards = rateCards;
  }  

  @Mutation
  private async setRateCardFromEdit(rateCard: RateCardDto) {
    //Retain the positions as they aren't returned from edit
    rateCard.positions = JSON.parse(JSON.stringify(this.currentRateCard.positions));
    this.currentRateCard = rateCard;
    const index = this.rateCards.findIndex((findRateCard: RateCardDto) => rateCard.id === findRateCard.id);
    this.rateCards.splice(index, 1, rateCard);
  }

  @Mutation
  private async setRateCardFromCreate(rateCard: RateCardDto) {
    this.rateCards.push(rateCard);
    if (this.rateCards.length === 1) {
      //TODO - this doesn't impact selection in list view so can't do currently unless there is only 1
      this.currentRateCard = rateCard;
    }
  }

  @Mutation
  private async setRateCardPositionFromEdit(rateCardPosition: RateCardPositionDto) {
    const index = this.currentRateCard.positions.findIndex((findRateCardPosition: RateCardPositionDto) => rateCardPosition.id === findRateCardPosition.id);
    this.currentRateCard.positions.splice(index, 1, rateCardPosition);
  }

  @Mutation
  private async setRateCardPositionFromCreate(rateCardPosition: RateCardPositionDto) {
    this.currentRateCard.positions.push(rateCardPosition);
  }

  @Mutation
  private async removeRateCardPositionFromDelete(rateCardPosition: RateCardPositionDto) {
    const rateCardPositionIndex = this.currentRateCard.positions.findIndex((findRateCardPosition: RateCardPositionDto) => rateCardPosition.id === findRateCardPosition.id);
    this.currentRateCard.positions.splice(rateCardPositionIndex, 1);
  }

  @Mutation
  private async setNoteCategories(noteCategories: NoteCategoryDto[]) {
      this.noteCategories = noteCategories;
  }

  @Mutation
  private async setNotes(notes: NoteDto[]) {
      this.notes = notes;
  }

  @Mutation
  private async setNoteFromCreate(note: NoteDto) {
      this.notes.unshift(note);
  }

  @Mutation
  private async setNoteFromEdit(note: NoteDto) {
      const index = this.notes.findIndex((findNote: NoteDto) => findNote.id === note.id)
      this.notes.splice(index, 1, note);
  }

  @Mutation
  private async setLoadingNotes(isLoading: boolean) {
      this.loadingNotes = isLoading;
  }

  @Mutation
  private async setLoadingBookings(isLoading: boolean) {
      this.loadingBookings = isLoading;
  }


  @Mutation
  private async setDocuments(documents: DocumentDto[]) {
      this.documents = documents;
  }

  @Mutation
  private async setDocumentFromUpload(document: DocumentDto) {
      this.documents.push(document);
  }

  @Mutation
  private async setDocumentFromEdit(document: DocumentDto) {
      const index = this.documents.findIndex((findDocument: DocumentDto) => findDocument.id === document.id)
      this.documents.splice(index, 1, document);
  }

  @Mutation
  private async setLoadingXeroContacts(isLoading: boolean) {
      this.loadingXeroContacts = isLoading;
  }

  @Mutation
  private async setXeroContactSearchResults(results: XeroContactSearchResponse) {
      this.xeroContactSearchResults = results;
  }

  @Mutation
  private async setLoadingDocuments(isLoadingDocuments: boolean) {
      this.loadingDocuments = isLoadingDocuments;
  }
}

export const CompanyDetailsModule = getModule(CompanyDetails)
